import { lazy } from 'react';
import { AppRoute } from './route.types';
import { ROLE_MAPS } from '@shared/constants/roles';
import { PLAN_MAPS } from '@shared/constants/plans';
import { Loadable } from '@components/lazy-load/loadable';
import { BULK_MESSAGING_BASE_PATH } from '@modules/bulk-messaging/bulk-messaging.constants';
const LogoutAndRedirect = Loadable(lazy(() => import('@modules/auth/common/logout-and-redirect')));
const DialerPage = Loadable(lazy(() => import('@modules/dialer-extension.page')));
const NumberReport = Loadable(lazy(() => import('@modules/analytics/number-report/number-report.page')));
const MyAccountPage = Loadable(lazy(() => import('@modules/my-account/my-account-page')));
const NumberReportDetail = Loadable(
  lazy(() => import('@modules/analytics/number-report-detail/number-report-detail.page'))
);
const MemberReport = Loadable(lazy(() => import('@modules/analytics/member-report/member-report.page')));
const MemberReportDetail = Loadable(
  lazy(() => import('@modules/analytics/member-report-detail/member-report-detail.page'))
);
const AiDashboard = Loadable(lazy(() => import('@modules/analytics/ai-dashboard/ai-dashboard.page')));
const AdvancedReport = Loadable(lazy(() => import('@modules/analytics/advanced-report/advanced-report.page')));
const MyNumberSettings = Loadable(lazy(() => import('@modules/settings/my-numbers/my-numbers.page')));
const CallerIDSettings = Loadable(lazy(() => import('@modules/settings/caller-id/caller-id.page')));
const PurchaseNumberSettings = Loadable(
  lazy(() => import('@modules/settings/my-numbers/purchase-number/purchase-number.page'))
);
const MembersSettings = Loadable(lazy(() => import('@modules/settings/members/members.page')));
const TeamManagementSettings = Loadable(lazy(() => import('@modules/settings/team-management/team-management.page')));
const TeamDetailSettings = Loadable(lazy(() => import('@modules/settings/team-detail/team-detail.page')));
const BlockListPage = Loadable(lazy(() => import('@modules/settings/block-list/block-list.page')));
const SavedReplies = Loadable(lazy(() => import('@modules/settings/saved-replies/saved-replies.page')));
const SubscriptionPage = Loadable(lazy(() => import('@modules/settings/subscription/subscription.page')));
const MyMfaPage = Loadable(lazy(() => import('@modules/my-account/features/my-mfa/my-mfa.page')));
const MyMfaVerifyPage = Loadable(lazy(() => import('@modules/my-account/features/my-mfa/my-mfa-verify.page')));

const OrderAndInvoicesPage = Loadable(
  lazy(() => import('@modules/settings/order-and-invoices/order.and.invoices.page'))
);
const ReasonForCalls = Loadable(lazy(() => import('@modules/analytics/reason-for-calls/reason-for-calls.page')));
const PositiveSentimentReport = Loadable(
  lazy(() => import('@modules/analytics/positive-sentiment-report/positive-sentiment-report.page'))
);
const NegativeSentimentReport = Loadable(
  lazy(() => import('@modules/analytics/negative-sentiment-report/negative-sentiment-report.page'))
);
const Wallet = Loadable(lazy(() => import('@modules/settings/wallet/wallet.page')));
const ConclusionForCalls = Loadable(
  lazy(() => import('@modules/analytics/conclusions-for-calls/conclusion-for-calls.page'))
);
const CallResolution = Loadable(lazy(() => import('@modules/analytics/call-resolution/call-resolution.page')));
const DashboardPage = Loadable(lazy(() => import('@modules/dashboard/dashboard.page')));
const CalilioHubPage = Loadable(lazy(() => import('@modules/calilio-hub/calilio-hub.page')));
const MyPackUsagePage = Loadable(lazy(() => import('@modules/calilio-hub/my-pack-usage.page')));
const CalilioHubOverviewPage = Loadable(lazy(() => import('@modules/calilio-hub/calilio-hub-overview.page')));
const CalilioHubRedirect = Loadable(lazy(() => import('@modules/calilio-hub/calilio-hub-redirect')));
const ToolsPage = Loadable(lazy(() => import('@modules/tools/tools.page')));
const MicAndAudioPage = Loadable(lazy(() => import('@modules/tools/mic-and-audio/mic-and-audio.page')));
const ConversationPage = Loadable(lazy(() => import('@modules/conversations/conversation.page')));
const ConversationRedirection = Loadable(lazy(() => import('@modules/conversations/conversation-redirection.page')));
const PaymentMethodsPage = Loadable(lazy(() => import('@modules/settings/payment-methods/payment-methods.page')));
const ContactsPage = Loadable(lazy(() => import('@modules/contacts/contacts.page')));
const MessagingConsentPage = Loadable(lazy(() => import('@modules/consents/messaging-consents.page')));
const BulkMessagingPage = Loadable(lazy(() => import('@modules/bulk-messaging/bulk-messaging.page')));
const ComposeMessagePage = Loadable(
  lazy(() => import('@modules/bulk-messaging/bulk-sms-message/compose-message/compose-message.page'))
);
const EditMessagePage = Loadable(lazy(() => import('@modules/bulk-messaging/components/edit-message.page')));
const BulkMessageReportPage = Loadable(
  lazy(() => import('@modules/bulk-messaging/bulk-message-report/bulk-message-report.page'))
);
const BulkMessageRunReportPage = Loadable(
  lazy(() => import('@modules/bulk-messaging/bulk-message-report/bulk-message-run-report.page'))
);
const RecurringCampaignPage = Loadable(
  lazy(() => import('@modules/bulk-messaging/recurring-campaign/recurring-campaign.page'))
);
const SupportTicket = Loadable(lazy(() => import('@modules/support-ticket.page')));
const RootRedirect = Loadable(lazy(() => import('@modules/auth/common/root-redirect')));
const NewConversationPage = Loadable(lazy(() => import('@modules/conversations/new-conversation.page')));
const MemberConversationPage = Loadable(
  lazy(() => import('@modules/conversations/features/member-conversation/member-conversation.page'))
);
const AllMembersConversationPage = Loadable(
  lazy(() => import('@modules/conversations/features/all-members-conversation/all-members-conversation.page'))
);
const Integrations = Loadable(lazy(() => import('@modules/settings/integrations/integrations.page')));
const IntegrationDetails = Loadable(
  lazy(() => import('@modules/settings/integrations/components/integration-details.page'))
);
const WorkspacePage = Loadable(lazy(() => import('@modules/settings/workspace/workspace.page')));

const AiVoiceAgent = Loadable(lazy(() => import('@modules/ai-assistance/ai-voice-agent/ai-voice-agent.page')));
const AiVoiceAgentDetail = Loadable(
  lazy(() => import('@modules/ai-assistance/ai-voice-agent/agent-detail/agent-detail.page'))
);
const AiSettingsPage = Loadable(lazy(() => import('@modules/ai-assistance/ai-settings/ai-settings.page')));
const KnowledgeSourcesPage = Loadable(
  lazy(() => import('@modules/ai-assistance/ai-settings/features/knowledge-sources/knowledge-sources.page'))
);
const SmartDialerPage = Loadable(lazy(() => import('@modules/ai-assistance/smart-dialer/smart-dialer.page')));

const NumberGroups = Loadable(lazy(() => import('@modules/settings/number-groups/number-groups.page')));
const PaymentRequestPage = Loadable(lazy(() => import('@modules/settings/order-and-invoices/payment-request.page')));

// NEW NUMBER SETTINGS PAGE
const CallFlowsList = Loadable(lazy(() => import('@modules/settings/call-settings-list/call-settings-list')));
const NumberSettingsPage = Loadable(lazy(() => import('@modules/settings/call-settings/number-settings.page')));
const CallSettingsPage = Loadable(lazy(() => import('@modules/settings/call-settings/call-settings.page')));
const BusinessHoursSettingsV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/business-hours/business-hours.page'))
);
const AfterHoursSettingsV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/after-hours/after-hours.page'))
);
const GreetingMessagePageV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/greeting-message/greeting-message.page'))
);
const IVRSettingsV2 = Loadable(lazy(() => import('@modules/settings/call-settings/features/ivr/ivr.page')));
const CallForwardingV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/call-forwarding/call-forwarding.page'))
);
const RingStrategyV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/ring-strategy/ring-strategy.page'))
);
const CallQueueV2 = Loadable(lazy(() => import('@modules/settings/call-settings/features/call-queue/call-queue.page')));
const VoicemailV2 = Loadable(lazy(() => import('@modules/settings/call-settings/features/voicemail/voicemail.page')));
const CustomCallerTuneV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/custom-caller-tune/caller-tune.page'))
);
const AutoRecordCallsV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/auto-record/auto-record.page'))
);
const CallTransferV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/call-transfer/call-transfer.page'))
);
const EmailNotificationAlertV2 = Loadable(
  lazy(() => import('@modules/settings/call-settings/features/email-notification-alert/email-notification-alert.page'))
);
const AppRedirectCenter = Loadable(lazy(() => import('@modules/app-redirection-center/app-redirection-center.page')));
const DownloadAppsPage = Loadable(lazy(() => import('@modules/tools/download-apps/download-apps.page')));
const MobileAppsPage = Loadable(lazy(() => import('@modules/tools/download-apps/mobile-apps/mobile-apps.page')));
const WebAppsPage = Loadable(lazy(() => import('@modules/tools/download-apps/web-apps/web-apps.page')));

const analytics: AppRoute[] = [
  {
    type: 'parent',
    path: '/analytics',
    children: [
      {
        type: 'parent',
        path: '/number-report',
        children: [
          {
            type: 'render',
            path: '/',
            component: NumberReport,
            breadcrumbs: [
              {
                title: 'Analytics',
              },
              {
                title: 'Number Report',
              },
            ],
          },
          {
            type: 'render',
            path: '/:channelId',
            component: NumberReportDetail,
            breadcrumbs: [
              {
                title: 'Analytics',
              },
              {
                title: 'Number Report',
                link: '/analytics/number-report',
              },
              {
                title: ':numberName',
              },
            ],
          },
        ],
      },
      {
        type: 'parent',
        path: '/member-report',
        children: [
          {
            type: 'render',
            path: '/',
            component: MemberReport,
            breadcrumbs: [
              {
                title: 'Analytics',
              },
              {
                title: 'Member Report',
              },
            ],
          },
          {
            type: 'render',
            path: '/:memberId',
            component: MemberReportDetail,
            breadcrumbs: [
              {
                title: 'Analytics',
              },
              {
                title: 'Member Report',
                link: '/analytics/member-report',
              },
              {
                title: ':memberName',
              },
            ],
          },
        ],
      },

      {
        type: 'parent',
        path: '/ai-dashboard',
        children: [
          {
            type: 'render',
            path: '/',
            component: AiDashboard,
            breadcrumbs: [
              {
                title: 'Analytics',
              },
              {
                title: 'AI Dashboard',
              },
            ],
          },
        ],
      },

      {
        type: 'render',
        path: '/advanced-report',
        component: AdvancedReport,
        breadcrumbs: [
          {
            title: 'Analytics',
          },
          {
            title: 'Advanced Report',
          },
        ],
      },

      {
        type: 'parent',
        path: '/reason-for-calls',
        children: [
          {
            type: 'render',
            path: '/',
            component: ReasonForCalls,
            breadcrumbs: [
              {
                title: 'Analytics',
              },
              {
                title: 'Reason For Calls',
              },
            ],
          },
        ],
      },
      {
        type: 'parent',
        path: '/conclusion-of-calls',
        children: [
          {
            type: 'render',
            path: '/',
            component: ConclusionForCalls,
            breadcrumbs: [
              {
                title: 'Analytics',
              },
              {
                title: 'Call Conclusions',
              },
            ],
          },
        ],
      },

      {
        type: 'render',
        path: '/call-resolution',
        component: CallResolution,
        breadcrumbs: [
          {
            title: 'Analytics',
          },
          {
            title: 'Call Resolution',
          },
        ],
      },

      {
        type: 'render',
        path: '/positive-neutral-sentiment',
        component: PositiveSentimentReport,
        breadcrumbs: [
          {
            title: 'Analytics',
          },
          {
            title: 'Positive/Neutral Sentiment',
          },
        ],
      },
      {
        type: 'render',
        path: '/negative-sentiment',
        component: NegativeSentimentReport,
        breadcrumbs: [
          {
            title: 'Analytics',
          },
          {
            title: 'Negative Sentiment',
          },
        ],
      },
    ],
  },
];

const callSettings: AppRoute[] = [
  // number settings
  {
    type: 'parent',
    path: '/channel',
    children: [
      {
        type: 'parent',
        path: '/:channelId',
        children: [
          {
            type: 'render',
            path: '/',
            component: NumberSettingsPage,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
              },
            ],
          },
          {
            type: 'render',
            path: '/business-hours',
            component: BusinessHoursSettingsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Business Hours',
                translationKey: 'business_hour_other',
              },
            ],
          },
          {
            type: 'render',
            path: '/after-hours',
            component: AfterHoursSettingsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'After Hours',
                translationKey: 'after_hours',
              },
            ],
          },
          {
            type: 'render',
            path: '/greeting-message',
            component: GreetingMessagePageV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Greeting Message',
                translationKey: 'greeting_message',
              },
            ],
          },
          {
            type: 'render',
            path: '/ivr',
            component: IVRSettingsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'IVR',
                translationKey: 'ivr',
              },
            ],
          },
          {
            type: 'render',
            path: '/call-forwarding',
            component: CallForwardingV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Call Forwarding',
                translationKey: 'call_forwarding',
              },
            ],
          },
          {
            type: 'render',
            path: '/ring-strategy',
            component: RingStrategyV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Ring Strategy',
                translationKey: 'ring_strategy',
              },
            ],
          },
          {
            type: 'render',
            path: '/call-queue',
            component: CallQueueV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Call Queue',
                translationKey: 'call_queue',
              },
            ],
          },
          {
            type: 'render',
            path: '/voicemail',
            component: VoicemailV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Voicemail',
                translationKey: 'voicemail',
              },
            ],
          },
          {
            type: 'render',
            path: '/custom-caller-tune',
            component: CustomCallerTuneV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Custom Caller Tune',
                translationKey: 'custom_caller_tune',
              },
            ],
          },
          {
            type: 'render',
            path: '/auto-record',
            component: AutoRecordCallsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Auto Record Calls',
                translationKey: 'auto_record_calls',
              },
            ],
          },
          {
            type: 'render',
            path: '/email-notification-alert',
            component: EmailNotificationAlertV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Email Notification Alert',
                translationKey: 'email_notification_alert',
              },
            ],
          },
          {
            type: 'render',
            path: '/call-transfer',
            component: CallTransferV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: ':name',
                link: '/settings/channel/:channelId',
              },
              {
                title: 'Call Transfer Announcements',
                translationKey: 'call_transfer_announcements',
              },
            ],
          },
        ],
      },
    ],
  },
  // call settings
  {
    type: 'parent',
    path: '/call-settings',
    children: [
      {
        type: 'render',
        path: '/',
        component: CallFlowsList,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Call Settings',
            translationKey: 'call_setting_other',
          },
        ],
      },
      {
        type: 'parent',
        path: '/:callSettingId',
        children: [
          {
            type: 'render',
            path: '/',
            component: CallSettingsPage,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
              },
            ],
          },
          {
            type: 'render',
            path: '/business-hours',
            component: BusinessHoursSettingsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Business Hours',
                translationKey: 'business_hour_other',
              },
            ],
          },
          {
            type: 'render',
            path: '/after-hours',
            component: AfterHoursSettingsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'After Hours',
                translationKey: 'after_hours',
              },
            ],
          },
          {
            type: 'render',
            path: '/greeting-message',
            component: GreetingMessagePageV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Greeting Message',
                translationKey: 'greeting_message',
              },
            ],
          },
          {
            type: 'render',
            path: '/ivr',
            component: IVRSettingsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'IVR',
                translationKey: 'ivr',
              },
            ],
          },
          {
            type: 'render',
            path: '/call-forwarding',
            component: CallForwardingV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Call Forwarding',
                translationKey: 'call_forwarding',
              },
            ],
          },
          {
            type: 'render',
            path: '/ring-strategy',
            component: RingStrategyV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Ring Strategy',
                translationKey: 'ring_strategy',
              },
            ],
          },
          {
            type: 'render',
            path: '/call-queue',
            component: CallQueueV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Call Queue',
                translationKey: 'call_queue',
              },
            ],
          },
          {
            type: 'render',
            path: '/voicemail',
            component: VoicemailV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Voicemail',
                translationKey: 'voicemail',
              },
            ],
          },
          {
            type: 'render',
            path: '/custom-caller-tune',
            component: CustomCallerTuneV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Custom Caller Tune',
                translationKey: 'custom_caller_tune',
              },
            ],
          },
          {
            type: 'render',
            path: '/auto-record',
            component: AutoRecordCallsV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Auto Record Calls',
                translationKey: 'auto_record_calls',
              },
            ],
          },
          {
            type: 'render',
            path: '/email-notification-alert',
            component: EmailNotificationAlertV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Email Notification Alert',
                translationKey: 'email_notification_alert',
              },
            ],
          },
          {
            type: 'render',
            path: '/call-transfer',
            component: CallTransferV2,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Call Settings',
                translationKey: 'call_setting_other',
                link: '/settings/call-settings',
              },
              {
                title: ':name',
                link: '/settings/call-settings/:callSettingId',
              },
              {
                title: 'Call Transfer Announcements',
                translationKey: 'call_transfer_announcements',
              },
            ],
          },
        ],
      },
    ],
  },
];

export const appPrivateRoutes: AppRoute[] = [
  {
    type: 'render',
    path: '/',
    component: RootRedirect,
  },
  {
    type: 'render',
    path: '/dashboard',
    component: DashboardPage,
    breadcrumbs: [
      {
        title: 'Dashboard',
      },
    ],
  },
  {
    type: 'parent',
    path: '/calilio-hub',
    children: [
      {
        type: 'render',
        path: '',
        component: CalilioHubRedirect,
      },
      {
        type: 'render',
        path: '/overview',
        component: CalilioHubOverviewPage,
        pageSize: 'medium',

        breadcrumbs: [
          {
            title: 'Calilio Hub',
          },
          {
            title: 'Overview',
          },
        ],
      },
      {
        type: 'render',
        path: '/voip-packs',
        component: CalilioHubPage,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Calilio Hub',
          },
          {
            title: 'VoIP Packs',
          },
        ],
      },
      {
        type: 'render',
        path: '/my-pack-usage',
        component: MyPackUsagePage,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Calilio Hub',
          },
          {
            title: 'My Pack Usage',
          },
        ],
      },
    ],
  },
  ...analytics,
  {
    type: 'parent',
    path: '/settings',
    children: [
      ...callSettings,
      {
        type: 'parent',
        path: '/my-profile',
        children: [
          {
            type: 'render',
            path: '/',
            component: MyAccountPage,
            rolePrivilege: ROLE_MAPS.SETTINGS_MY_ACCOUNT_DETAIL,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Profile',
                translationKey: 'my_profile',
              },
            ],
          },
          {
            type: 'render',
            path: '/mfa',
            component: MyMfaPage,
            rolePrivilege: ROLE_MAPS.SETTINGS_MY_ACCOUNT_DETAIL,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Profile',
                translationKey: 'my_profile',
                link: '/settings/my-profile',
              },
              {
                title: 'Two Factor Authentication',
                translationKey: 'two_factor_authentication',
              },
            ],
          },
          {
            type: 'render',
            path: '/mfa/verify',
            component: MyMfaVerifyPage,
            rolePrivilege: ROLE_MAPS.SETTINGS_MY_ACCOUNT_DETAIL,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Profile',
                translationKey: 'my_profile',
                link: '/settings/my-profile',
              },
              {
                title: 'Two Factor Authentication',
                translationKey: 'two_factor_authentication',
                link: '/settings/my-profile/mfa',
              },
              {
                title: 'Verify',
                translationKey: 'verify',
              },
            ],
          },
        ],
      },
      {
        type: 'parent',
        path: '/my-numbers',
        children: [
          {
            type: 'render',
            path: '/',
            component: MyNumberSettings,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
              },
            ],
          },
          {
            type: 'render',
            path: '/purchase-number',
            component: PurchaseNumberSettings,
            rolePrivilege: ROLE_MAPS.SETTINGS_MY_NUMBERS_PURCHASE_NUMBER,
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'My Numbers',
                translationKey: 'my_number_other',
                link: '/settings/my-numbers',
              },
              {
                title: 'Purchase Number',
                translationKey: 'purchase_number',
              },
            ],
          },
        ],
      },
      {
        type: 'render',
        path: '/caller-id',
        component: CallerIDSettings,
        rolePrivilege: ROLE_MAPS.SETTINGS_CALLER_ID_LIST,
        planPrivilege: PLAN_MAPS.SETTINGS_CALLER_ID_LIST,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Caller ID',
            translationKey: 'caller_id_other',
          },
        ],
      },
      {
        type: 'render',
        path: '/number-groups',
        component: NumberGroups,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Number Groups',
            translationKey: 'number_group_other',
          },
        ],
      },
      {
        type: 'render',
        path: '/members',
        component: MembersSettings,
        rolePrivilege: ROLE_MAPS.SETTINGS_MEMBERS_LIST,
        pageSize: 'medium',
        breadcrumbs: [
          { title: 'Settings', translationKey: 'settings' },
          { title: 'Members', translationKey: 'member_other' },
        ],
      },
      {
        type: 'parent',
        path: '/team-management',
        children: [
          {
            type: 'render',
            path: '/',
            component: TeamManagementSettings,
            planPrivilege: PLAN_MAPS.TEAMS_TEAMS_LIST,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Teams',
                translationKey: 'team_other',
              },
            ],
          },
          {
            type: 'render',
            path: '/:teamId',
            component: TeamDetailSettings,
            planPrivilege: PLAN_MAPS.TEAMS_TEAMS_VIEW,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Teams',
                translationKey: 'team_other',
                link: '/settings/team-management',
              },
              {
                title: ':name',
              },
            ],
          },
        ],
      },
      {
        type: 'render',
        path: '/block-list',
        component: BlockListPage,
        rolePrivilege: ROLE_MAPS.SETTINGS_BLOCK_LISTS_LIST,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Block List',
            translationKey: 'block_list',
          },
        ],
      },
      {
        type: 'render',
        path: '/saved-replies',
        component: SavedReplies,
        planPrivilege: PLAN_MAPS.SETTINGS_NUMBER_SETTING_VIEW_SAVE_REPLIES,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Saved Replies',
            translationKey: 'saved_replies',
          },
        ],
      },
      {
        type: 'render',
        path: '/subscription',
        component: SubscriptionPage,
        rolePrivilege: ROLE_MAPS.SETTINGS_SUBSCRIPTION_VIEW,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Subscription',
            translationKey: 'subscription',
          },
        ],
      },
      {
        type: 'render',
        path: '/payment-methods',
        component: PaymentMethodsPage,
        rolePrivilege: ROLE_MAPS.SETTINGS_PAYMENT_METHOD_VIEW,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Payment Methods',
            translationKey: 'payment_method_other',
          },
        ],
      },
      {
        type: 'render',
        path: '/wallet',
        component: Wallet,
        rolePrivilege: ROLE_MAPS.SETTINGS_WALLET_VIEW,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Wallet',
            translationKey: 'wallet',
          },
        ],
      },
      {
        type: 'render',
        path: '/order-and-transactions',
        component: OrderAndInvoicesPage,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Order and Transactions',
            translationKey: 'order_and_transactions',
          },
        ],
      },
      {
        type: 'render',
        path: '/workspace',
        component: WorkspacePage,
        pageSize: 'medium',
        breadcrumbs: [
          {
            title: 'Settings',
            translationKey: 'settings',
          },
          {
            title: 'Workspace',
            translationKey: 'workspace',
          },
        ],
      },
      {
        type: 'parent',
        path: '/integrations',
        children: [
          {
            type: 'render',
            path: '/',
            component: Integrations,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Integrations',
                translationKey: 'integration_other',
              },
            ],
          },
          {
            type: 'render',
            path: '/:id',
            component: IntegrationDetails,
            pageSize: 'medium',
            breadcrumbs: [
              {
                title: 'Settings',
                translationKey: 'settings',
              },
              {
                title: 'Integrations',
                translationKey: 'integration_other',
                link: '/settings/integrations',
              },
              {
                title: ':name',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    type: 'parent',
    path: '/conversations',
    children: [
      {
        type: 'parent',
        path: '/:channelId',
        children: [
          {
            type: 'render',
            path: '/:clientId',
            component: ConversationPage,
          },
          {
            type: 'render',
            path: '/',
            component: ConversationRedirection,
          },
          {
            type: 'render',
            path: '/new',
            component: NewConversationPage,
          },
        ],
      },
      {
        type: 'render',
        path: '/member/:memberId',
        component: MemberConversationPage,
      },
      {
        // one group room per workspace, found-or-created by the backend, so no id in the path
        type: 'render',
        path: '/all-members',
        component: AllMembersConversationPage,
      },
    ],
  },
  {
    type: 'parent',
    path: BULK_MESSAGING_BASE_PATH,
    children: [
      {
        type: 'render',
        path: '/',
        breadcrumbs: [
          { title: 'Tools' },
          {
            title: 'Bulk Messaging',
          },
        ],
        component: BulkMessagingPage,
        rolePrivilege: ROLE_MAPS.BULK_MESSAGING_CAMPAIGNS_VIEW,
      },
      {
        type: 'render',
        path: '/compose-message',
        component: ComposeMessagePage,
        rolePrivilege: ROLE_MAPS.BULK_MESSAGING_CAMPAIGNS_CREATE,
      },
      {
        type: 'render',
        path: '/:campaignId/edit-message',
        component: EditMessagePage,
        rolePrivilege: ROLE_MAPS.BULK_MESSAGING_CAMPAIGNS_UPDATE,
      },
      {
        type: 'render',
        path: '/recurring/:campaignId',
        breadcrumbs: [
          { title: 'Tools' },
          {
            title: 'Bulk Messaging',
            link: BULK_MESSAGING_BASE_PATH,
          },
          {
            title: ':name',
          },
        ],
        component: RecurringCampaignPage,
        rolePrivilege: ROLE_MAPS.BULK_MESSAGING_CAMPAIGNS_LIST,
      },
      {
        type: 'render',
        path: '/recurring/:campaignId/:runId',
        breadcrumbs: [
          { title: 'Tools' },
          {
            title: 'Bulk Messaging',
            link: BULK_MESSAGING_BASE_PATH,
          },
          {
            title: ':name',
            link: `${BULK_MESSAGING_BASE_PATH}/recurring/:campaignId`,
          },
          {
            title: ':runName',
          },
        ],
        component: BulkMessageRunReportPage,
        rolePrivilege: ROLE_MAPS.BULK_MESSAGING_CAMPAIGNS_LIST,
      },
      {
        type: 'render',
        path: '/:campaignId',
        breadcrumbs: [
          { title: 'Tools' },
          {
            title: 'Bulk Messaging',
            link: BULK_MESSAGING_BASE_PATH,
          },
          {
            title: ':name',
          },
        ],
        component: BulkMessageReportPage,
        rolePrivilege: ROLE_MAPS.BULK_MESSAGING_CAMPAIGNS_LIST,
      },
    ],
  },
  {
    type: 'parent',
    path: '/contacts',
    children: [
      {
        type: 'render',
        path: '/',
        breadcrumbs: [
          {
            title: 'Contacts',
          },
        ],
        component: ContactsPage,
        rolePrivilege: ROLE_MAPS.CONTACTS_CONTACTS_LIST,
      },
      {
        type: 'render',
        path: '/messaging-consents',
        breadcrumbs: [
          {
            title: 'Contacts',
            link: '/contacts',
          },
          {
            title: 'Messaging Consents',
          },
        ],
        component: MessagingConsentPage,
      },
    ],
  },
  {
    // TODO: add rolePrivilege/planPrivilege once AI Assistance role/plan maps exist in ROLE_MAPS/PLAN_MAPS
    type: 'parent',
    path: '/ai-assistance',
    children: [
      {
        type: 'parent',
        path: '/ai-voice-agent',
        children: [
          {
            type: 'render',
            path: '/',
            component: AiVoiceAgent,
            pageSize: 'medium',
            breadcrumbs: [{ title: 'AI Assistance' }, { title: 'AI Voice Agent' }],
          },
          {
            type: 'render',
            path: '/:agentId',
            component: AiVoiceAgentDetail,
            pageSize: 'medium',
            // ':name' resolves from the `?name=` search param (see app-bread-crumbs.tsx)
            breadcrumbs: [{ title: 'AI Assistance', link: '/ai-assistance/ai-voice-agent' }, { title: ':name' }],
          },
        ],
      },
      {
        type: 'parent',
        path: '/ai-settings',
        children: [
          {
            type: 'render',
            path: '/',
            component: AiSettingsPage,
            pageSize: 'medium',
            breadcrumbs: [{ title: 'AI Assistance' }, { title: 'AI Settings' }],
          },
          {
            type: 'render',
            path: '/knowledge-sources',
            component: KnowledgeSourcesPage,
            pageSize: 'medium',
            breadcrumbs: [
              { title: 'AI Assistance' },
              { title: 'AI Settings', link: '/ai-assistance/ai-settings' },
              { title: 'Knowledge Sources' },
            ],
          },
        ],
      },
      {
        type: 'render',
        path: '/smart-dialer',
        component: SmartDialerPage,
        pageSize: 'medium',
        breadcrumbs: [{ title: 'AI Assistance' }, { title: 'Smart Dialer' }],
      },
    ],
  },
  {
    // TODO: add rolePrivilege/planPrivilege once Tools role/plan maps exist in ROLE_MAPS/PLAN_MAPS
    type: 'parent',
    path: '/tools',
    children: [
      {
        type: 'render',
        path: '',
        component: ToolsPage,
        breadcrumbs: [{ title: 'Tools' }],
      },
      {
        type: 'render',
        path: '/download-apps',
        component: DownloadAppsPage,
        pageSize: 'medium',
        breadcrumbs: [{ title: 'Tools' }, { title: 'Downloads' }],
      },
      {
        type: 'render',
        path: '/download-apps/mobile-apps',
        component: MobileAppsPage,
        pageSize: 'medium',
        breadcrumbs: [
          { title: 'Tools' },
          { title: 'Downloads', link: '/tools/download-apps' },
          { title: 'Mobile Apps' },
        ],
      },
      {
        type: 'render',
        path: '/download-apps/web-apps',
        component: WebAppsPage,
        pageSize: 'medium',
        breadcrumbs: [{ title: 'Tools' }, { title: 'Downloads', link: '/tools/download-apps' }, { title: 'Web Apps' }],
      },
      {
        type: 'render',
        path: '/mic-and-audio',
        component: MicAndAudioPage,
        pageSize: 'medium',
        breadcrumbs: [
          { title: 'Tools', translationKey: 'tools' },
          { title: 'Other Tools', translationKey: 'other_tools' },
          { title: 'Mic and Audio', translationKey: 'mic_and_audio' },
        ],
      },
    ],
  },
  {
    type: 'render',
    path: '/support-ticket',
    component: SupportTicket,
  },
  {
    type: 'render',
    path: '/payment-request',
    component: PaymentRequestPage,
  },
  {
    type: 'render',
    path: '*',
    component: RootRedirect,
    breadcrumbs: [{ title: 'Not Found' }],
  },
];

export const basePrivateRoutes: AppRoute[] = [
  {
    type: 'render',
    path: '/app-redirect/:mode',
    component: AppRedirectCenter,
  },
  {
    type: 'render',
    path: '/route',
    component: AppRedirectCenter,
  },
  {
    type: 'render',
    path: '/auth/invitation/:token',
    component: LogoutAndRedirect,
  },
  {
    type: 'render',
    path: '/app/dialer',
    component: DialerPage,
  },
];
